import { encData } from 'js/utils/binary.util';
import { addParamsToUrl } from 'js/utils/url.util';
import { IPaymentAccountField } from 'js/hooks/socket/types';
import {
    CASHIER_TYPE,
    CASHIER_FIELD_TYPE,
    CASHIER_REDIRECT_TYPE,
    PAYMENT_METHODS_CODES
} from 'js/hooks/socket/constants';

import {
    IBackURLs, IKnownError, ICashierError, IParsedAccount, TCashierFormValues,
    IGetBackUrlsProps, ICashierErrorResponse, TCashierFormValue, IParsedPaymentMethod
} from '../types';
import { KNOWN_ERRORS, IMS_CHECKBOX_FIELD_VALUE, CREDIT_CARD } from '../constants';

export const getBackUrls = (data: IGetBackUrlsProps): IBackURLs => {
    let extraData;
    const { amount, promoCode, code, action, themeName, reDirectType, initiator, sidebarPageURL } = data;

    if (promoCode) {
        extraData = encData({ promocode: promoCode });
    }

    const urlPaymentPage = new window.URL(`${window.location.origin}/${themeName}/js/app-out/payment.html`);
    const defaultBackUrl = reDirectType === CASHIER_REDIRECT_TYPE.POPUP || reDirectType === CASHIER_REDIRECT_TYPE.IFRAME
        ? urlPaymentPage
        : window.location;
    const params = {
        amount,
        action,
        initiator,
        type: code,
        redirectType: reDirectType,
        ...sidebarPageURL && { sidebarPageURL }
    };

    return {
        successUrl: addParamsToUrl({ ...params, status: 'success', extraData }, defaultBackUrl),
        errorUrl: addParamsToUrl({ ...params, status: 'error' }, defaultBackUrl),
        cancelUrl: addParamsToUrl({ ...params, status: 'cancel' }, defaultBackUrl),
        waitingUrl: addParamsToUrl({ ...params, status: 'waiting' }, defaultBackUrl),
        ...action === CASHIER_TYPE.DEPOSIT && {
            threeDSErrorURL: addParamsToUrl({ ...params, status: '3DsError' }, urlPaymentPage),
            threeDSSuccessURL: addParamsToUrl({ ...params, status: '3DsSuccess' }, urlPaymentPage),
            threeDSMethodNotificationURL: addParamsToUrl({ ...params, status: '3DsNotification' }, urlPaymentPage)
        }
    };
};

export const getKnownError = (error?: ICashierError): IKnownError | undefined => {
    return KNOWN_ERRORS.find(knownError => knownError.code === error?.code);
};

export const isPekoResponse = (response: ICashierErrorResponse) => {
    const error = response?.data?.error;

    return error && !error?.originally;
};

export const getPrePaymentPageURL = (themeName: string) =>
    `${window.location.origin}/${themeName}/js/app-out/loading.html`;

export const resolveStoredAccount = (
    { data, account, isGeneric }:
    { data: TCashierFormValues; account?: IParsedAccount; isGeneric?: boolean }
): TCashierFormValues & { accountId?: string } => {
    const { storedAccountId: accountCode } = data;

    if (!accountCode) {
        return data;
    }

    return {
        ...data,
        ...account?.specificParams,
        ...!isGeneric && { accountCode }
    };
};

export const parseCashierFormValues = (
    data: TCashierFormValues, accountFields: IPaymentAccountField[]
): TCashierFormValues => {
    const checkboxFields = accountFields
        .filter(field => field.type === CASHIER_FIELD_TYPE.CHECKBOX)
        .map(field => field.name);

    return Object.entries(data)
        .reduce((acc: { [k: string]: TCashierFormValue }, [key, value]) => {
            if (checkboxFields.includes(key)) {
                return {
                    ...acc,
                    [key]: value ? IMS_CHECKBOX_FIELD_VALUE.CHECKED : IMS_CHECKBOX_FIELD_VALUE.UNCHECKED
                };
            }

            return {
                ...acc,
                [key]: value ?? ''
            };
        }, {});
};

export const getIsNuveiRealCard = ({ depositType, withdrawType, reDirectType, code }: Partial<IParsedPaymentMethod>): boolean =>
    (depositType === CREDIT_CARD || withdrawType === CREDIT_CARD)
    && reDirectType === CASHIER_REDIRECT_TYPE.JS_NUVEI
    && code !== PAYMENT_METHODS_CODES.NUVEI_CC;

export const isApplePaySupported = (): boolean => Boolean(window.ApplePaySession?.canMakePayments?.());
